import { lazy, Suspense, useEffect, useRef } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from "react-router-dom";
import "./components/styles/common.css";
import { ConfigProvider } from "antd";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import HideLayoutProvider from "./components/auth/SetHideLayoutOnAuth";
import LoginPage from "./components/auth/LoginPage";
import CallbackPage from "./components/auth/Callback";
import EmailVerificationPage from "./components/auth/EmailVerificationPage";
import NewUserEmailVerificationPage from "./components/auth/NewUserEmailVerificationPage";
import { Stdconsole } from "./utils/setConsole";
import ErrorPage from "./components/errors/CollegeLimitExceeded";
import SessionExpired from "./components/errors/SessionExpired";
import { initPosthog } from "./posthog";
import { store } from "./redux/store";
import LayoutWrapper from "./LayoutWrapper";
import NetworkErrorPage from "./components/NetworkErrorPage";
import { RouteChunkError, clearReloadFlag } from "./components/ChunkErrorBoundary";
import { disconnectSocket, initSocket } from "./services/socketService";
import { useAuth } from "./hooks/useAuth";

// Clears the chunk-reload flag on every successful navigation so each route
// gets its own one-auto-reload budget instead of sharing one per session.
function NavResetOutlet() {
  useEffect(() => { clearReloadFlag(); });
  return <Outlet />;
}

const CollegeDetails = lazy(() => import("./components/collegeDetails/CollegeDetails"));
const MobileVerification = lazy(() => import("./components/MobileVerification"));
const Onboarding = lazy(() => import("./components/Onboarding"));
const SearchPage = lazy(() => import("./components/SearchPage"));
const ShortlisPage = lazy(() => import("./components/ShortlistPage"));
const MyProfilePage = lazy(() => import("./components/MyProfilePage"));
const FastTrackPage = lazy(() => import("./components/fastrackPage"));
const TalkToExpert = lazy(() => import("./components/TalkToExpert"));
const RecommendationPage = lazy(() => import("./components/RecommendationPage"));
const ComparePage = lazy(() => import("./components/ComparePage"));
const UpgradePlan = lazy(() => import("./components/PricingPlans"));
const SearchContent = lazy(() => import("./components/SearchUpdatedPage"));
const CoursePage = lazy(() => import("./components/CoursePage"));
const CookiePolicy = lazy(() => import("./components/CookiePolicyPage"));
const SignupPage = lazy(() => import("./components/auth/Signup"));
const ReferralPage = lazy(() => import("./components/Referral"));
const SettingsPage = lazy(() => import("./components/SettingsPage"));
const HomePage = lazy(() => import("./components/HomePage"));
const RolePage = lazy(() => import("./components/RolePage"));
const RoleDetails = lazy(() => import("./components/RoleDetails"));
// const ChatPage = lazy(() => import("./components/chat/ChatPage"));

const routesConfig = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/signup",
    element: <Suspense fallback={null}><SignupPage /></Suspense>,
  },
  {
    path: "/callback",
    element: <CallbackPage />,
  },
  {
    path: "/verify-email",
    element: <EmailVerificationPage />
  },
  {
    path: "/error-mismatched-login",
    element: <ErrorPage messageText="This email is already registered with another provider. Please log in using the correct method." showLogin />
  },
  {
    path: "/error-page-not-found",
    element: <ErrorPage messageText=" Sorry, we couldn't find what you're looking for!" showGoBack />
  },
  {
    path: "/error-college-limit-exceeded",
    element: <ErrorPage messageText="You have reached the maximum limit. Upgrade to view more colleges." showUpgrade showGoBack is429Error={false} />,
  },
  {
    path: "/error-college-limit-exceeded-mobile-unverified",
    element: <ErrorPage messageText="You have reached the maximum limit. verify your mobile number to view more colleges." showMobileVerification showGoBack is429Error={true} />,
  },
  {
    path: "/error-college-limit-exceeded-upgrade",
    element: <ErrorPage messageText="You have reached the maximum limit. Upgrade to view more colleges." showUpgrade showGoBack is429Error={true} />,
  },
  {
    path: "/error-course-limit-exceeded",
    element: <ErrorPage messageText="You have reached the maximum limit. Upgrade to view more courses" showUpgrade showGoBack is429Error={false} />,
  },
  {
    path: "/error-course-limit-exceeded-mobile-unverified",
    element: <ErrorPage messageText="You have reached the maximum limit. verify your mobile number to view more courses." showMobileVerification showGoBack is429Error={true} />,
  },
   {
    path: "/error-course-limit-exceeded-upgrade",
    element: <ErrorPage messageText="You have reached the maximum limit. Upgrade to view more courses." showUpgrade showGoBack is429Error={true} />,
  },
  {
    path: "/internal-server-error",
    element: <ErrorPage messageText="Oops! Something went wrong. Please try again later." showLogout />
  },
  {
    path: "/error-server-maintenance",
    element: <ErrorPage messageText="Server Under Maintenance, Please Try Again Later" showLogout />
  },
  {
    path: "/network-error",
    element: <NetworkErrorPage />
  },
  {
    path: "/session-expired",
    element: <SessionExpired />
  },
  {
    path: "/new-user-verify-email",
    element: <NewUserEmailVerificationPage />,
  },
  {
    element: (
      <HideLayoutProvider>
        <LayoutWrapper />
      </HideLayoutProvider>
    ),
    children: [
      {
        path: "cookie-policy",
        element: <Suspense fallback={null}><CookiePolicy /></Suspense>,
      },
      {
        path: "/",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <HomePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "search",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SearchContent />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // {
      //   path: "chat",
      //   element: (
      //     <ProtectedRoute>
      //       <Suspense fallback={null}>
      //         <ChatPage />
      //       </Suspense>
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: "/college",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SearchPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/course",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CoursePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/role",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RolePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/admission",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from={"admission"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/ranking",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from={"ranking"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/sports",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from={"sports"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/similar-colleges",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from="similarCollege" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/course/:courseSlug/similar-courses",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from="similarCourse" isCourseDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/fastrack",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <FastTrackPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: "/talk-to-expert",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <TalkToExpert />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: "/referral",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ReferralPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: "/college/:slug/course",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from={"course"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/course/:courseSlug",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails isCourseDetails={true} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/college/:slug/course/:courseSlug/ranking",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CollegeDetails from={"ranking"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/role/:roleSlug",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RoleDetails />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/role/:roleSlug/similar-roles",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RoleDetails from="similarRoles" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "/role/:roleSlug/similar-courses",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RoleDetails from="similarCourses" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "mobile-verification",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <MobileVerification />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "onboard",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <Onboarding />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "recommend-college-dream",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RecommendationPage from={"dream"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "recommend-college-target",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RecommendationPage from={"target"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "recommend-college-safe",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RecommendationPage from={"safe"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "recommend-course",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <RecommendationPage from={"course"} />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "match",
        element: <Navigate to="/recommend-college-dream" replace />,
      },
      {
        path: "my-list",
        element: <Navigate to="/mylist-college" replace />,
      },
      {
        path: "mylist-college",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ShortlisPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "mylist-course",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ShortlisPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "my-profile",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <MyProfilePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SettingsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "compare-college",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ComparePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "compare-course",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <ComparePage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "upgrade",
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <UpgradePlan />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <Navigate to="/error-page-not-found" replace />,
      }
    ],

  },
];

const router = createBrowserRouter([
  {
    element: <NavResetOutlet />,
    errorElement: <RouteChunkError />,
    children: routesConfig,
  },
]);

function App() {
  const { getToken } = useAuth();
  const didInitSocket = useRef(false);

  useEffect(() => {
    initPosthog();
    Stdconsole(store);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (didInitSocket.current || cancelled) return;
      didInitSocket.current = true;
      await initSocket(getToken);
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [getToken]);


  useEffect(() => {
    return () => {
      disconnectSocket();
    };
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#0056D2', fontFamily: 'Poppins, sans-serif' },
        components: { Menu: { colorPrimary: '#0056D2' } },
      }}
    >
      <Suspense fallback={null}>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </Suspense>
    </ConfigProvider>
  );
}


export default App;
